import { useMemo } from "react";
import type { CarProject, ChatSession } from "../api/client";

interface CarProjectListProps {
  projects: CarProject[];
  sessions: ChatSession[];
  activeCarSlug: string | null;
  activeSessionId: string | null;
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onSelectProject: (carSlug: string | null) => void;
  onSelectSession: (sessionId: string) => void;
  onCreateSessionForCar: (carSlug: string | null) => void;
  onDeleteSession: (sessionId: string) => void;
  onAddCar: () => void;
}

interface ProjectRow {
  slug: string | null;
  name: string;
  sessions: ChatSession[];
  lastMessageAt: string | null;
}

export function CarProjectList({
  projects,
  sessions,
  activeCarSlug,
  activeSessionId,
  isOpen,
  isLoading,
  onClose,
  onSelectProject,
  onSelectSession,
  onCreateSessionForCar,
  onDeleteSession,
  onAddCar,
}: CarProjectListProps) {
  const rows = useMemo(() => buildRows(projects, sessions), [projects, sessions]);

  return (
    <>
      <aside className={`session-drawer ${isOpen ? "open" : ""}`}>
        <div className="drawer-header">
          <div className="sidebar-brand">
            <div className="sidebar-logo">D</div>
            <div>
              <p className="eyebrow">dote hermes</p>
              <h2>车大师</h2>
            </div>
          </div>
          <button className="icon-button mobile-only" type="button" onClick={onClose}>
            关闭
          </button>
        </div>

        <button className="primary-button new-session" type="button" onClick={onAddCar}>
          + 添加车型
        </button>

        <p className="section-label">我的车型</p>
        <div className="project-list">
          {isLoading ? <p className="muted">加载项目中...</p> : null}
          {!isLoading && rows.length === 0 ? (
            <p className="muted">还没有项目，先添加一辆车开始。</p>
          ) : null}
          {rows.map((row) => {
            const isActive = row.slug === activeCarSlug;
            return (
              <div
                key={row.slug ?? "__unassigned__"}
                className={`project-card ${isActive ? "active" : ""}`}
              >
                <button
                  type="button"
                  className="project-card-header"
                  onClick={() => onSelectProject(row.slug)}
                >
                  <div className="project-card-title">
                    <span className="project-card-name">{row.name}</span>
                    <span className="project-card-meta">
                      {row.sessions.length} 个对话
                      {row.lastMessageAt ? ` · ${formatSessionTime(row.lastMessageAt)}` : ""}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="project-add-session"
                    aria-label="新建对话"
                    onClick={(event) => {
                      event.stopPropagation();
                      onCreateSessionForCar(row.slug);
                    }}
                  >
                    +
                  </button>
                </button>
                {isActive && row.sessions.length > 0 ? (
                  <div className="project-session-list">
                    {row.sessions.map((session) => (
                      <button
                        key={session.id}
                        type="button"
                        className={`session-item ${
                          session.id === activeSessionId ? "active" : ""
                        }`}
                        onClick={() => onSelectSession(session.id)}
                      >
                        <span>{session.title || "新对话"}</span>
                        <small>
                          {formatSessionTime(session.last_message_at || session.created_at)}
                        </small>
                        <span
                          className="delete-session"
                          role="button"
                          tabIndex={0}
                          onClick={(event) => {
                            event.stopPropagation();
                            onDeleteSession(session.id);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              event.stopPropagation();
                              onDeleteSession(session.id);
                            }
                          }}
                        >
                          删除
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </aside>
      {isOpen ? <button className="drawer-scrim" type="button" onClick={onClose} /> : null}
    </>
  );
}

function buildRows(projects: CarProject[], sessions: ChatSession[]): ProjectRow[] {
  // Start from the server-side aggregate so we keep zero-session projects too
  // (e.g. a freshly-added car waiting for its first message). Then attach the
  // session list locally — single round-trip per turn.
  const sessionsBySlug = new Map<string | null, ChatSession[]>();
  for (const session of sessions) {
    const key = session.car_slug ?? null;
    const list = sessionsBySlug.get(key) ?? [];
    list.push(session);
    sessionsBySlug.set(key, list);
  }
  for (const list of sessionsBySlug.values()) {
    list.sort((a, b) => sessionTime(b) - sessionTime(a));
  }

  const fromAggregate: ProjectRow[] = projects.map((project) => ({
    slug: project.car_slug,
    name: project.car_name,
    sessions: sessionsBySlug.get(project.car_slug) ?? [],
    lastMessageAt: project.last_message_at,
  }));

  // Any sessions whose slug isn't in the projects aggregate (e.g. created in
  // the current tab and not yet refetched) get folded in here so they show up
  // immediately without waiting for the next /projects round-trip.
  const seen = new Set(fromAggregate.map((row) => row.slug));
  for (const [slug, list] of sessionsBySlug) {
    if (seen.has(slug)) {
      continue;
    }
    fromAggregate.push({
      slug,
      name: slug ?? "未归类",
      sessions: list,
      lastMessageAt: list[0]?.last_message_at ?? list[0]?.created_at ?? null,
    });
  }

  fromAggregate.sort((a, b) => {
    const at = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
    const bt = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
    if (at !== bt) {
      return bt - at;
    }
    return a.name.localeCompare(b.name, "zh");
  });
  return fromAggregate;
}

function sessionTime(session: ChatSession): number {
  const raw = session.last_message_at || session.created_at;
  const t = raw ? new Date(raw).getTime() : 0;
  return Number.isNaN(t) ? 0 : t;
}

function formatSessionTime(value: string | null): string {
  if (!value) {
    return "";
  }
  try {
    return new Intl.DateTimeFormat("zh-CN", {
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return "";
  }
}
